<template>
  <div>
    <static-alert v-if="message" :message="message" @on-hide="message=''" />

    <!-- Add/Edit Form -->
    <transition mode="out-in">
      <div v-if="loading" class="text-center py-3">
        <loading-spinner />
      </div>
      <div v-else-if="showForm" class="card mb-4">
        <div class="card-body">
          <h6 class="card-title">{{ editingId ? 'Edit Location' : 'Add New Location' }}</h6>
          <form @submit.prevent="saveLocation">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Category <span class="text-danger">*</span></label>
                <source-categories v-model="formData.source_category_id" :enabled="true" />
              </div>
              <div class="col-md-6">
                <label class="form-label">Location Name <span class="text-danger">*</span></label>
                <input type="text" v-model="formData.name" class="form-control" required />
              </div>
              <div class="col-md-12">
                <label class="form-label">Address</label>
                <input type="text" v-model="formData.address" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label">City</label>
                <input type="text" v-model="formData.city" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label">State</label>
                <input type="text" v-model="formData.state" class="form-control" />
              </div>
              <div class="col-md-4">
                <label class="form-label">Country</label>
                <input type="text" v-model="formData.country" class="form-control" />
              </div>
              <div class="col-md-12">
                <label class="form-label">Notes</label>
                <textarea v-model="formData.notes" class="form-control" rows="2"></textarea>
              </div>
            </div>
            <div class="text-end mt-3">
              <button type="button" @click="cancelForm" class="btn btn-link">Cancel</button>
              <button type="submit" class="btn btn-primary ms-2">
                {{ editingId ? 'Update' : 'Add' }} Location
              </button>
            </div>
          </form>
        </div>
      </div>
      <div v-else class="d-flex justify-content-end gap-2 mb-3">
        <template v-if="mergeMode">
          <button class="btn btn-outline-secondary" @click="exitMergeMode">
            <i class="bi bi-x-lg"></i> Cancel Merge
          </button>
          <button
            class="btn btn-warning"
            :disabled="selectedLocations.length < 2 || mergeLoading"
            @click="openMergeModal"
          >
            <span v-if="mergeLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrows-collapse"></i>
            Merge {{ selectedLocations.length > 0 ? `(${selectedLocations.length})` : '' }}
          </button>
        </template>
        <template v-else>
          <button
            v-if="locationsList.length > 1"
            class="btn btn-outline-secondary"
            @click="enterMergeMode"
          >
            <i class="bi bi-arrows-collapse"></i> Merge Locations
          </button>
          <button class="btn btn-primary" @click="showAddForm">
            <i class="bi bi-plus-circle"></i> Add New Location
          </button>
        </template>
      </div>
    </transition>

    <!-- Locations List -->
    <div v-if="locationsList.length > 0" class="card">
      <div class="card-body">
        <h6 class="card-title">Your Source Locations</h6>
        <div class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th v-if="mergeMode" style="width: 40px"></th>
                <th>Name</th>
                <th>Category</th>
                <th>City</th>
                <th>State</th>
                <th v-if="!mergeMode">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="location in locationsList"
                :key="location.id"
                :class="{
                  'table-active': mergeMode && isLocationSelected(location),
                  'text-muted': mergeMode && isLocationDisabled(location)
                }"
              >
                <td v-if="mergeMode">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="isLocationSelected(location)"
                    :disabled="isLocationDisabled(location)"
                    @change="toggleLocationSelection(location)"
                  />
                </td>
                <td>{{ location.name }}</td>
                <td>{{ getCategoryName(location.category_id) }}</td>
                <td>{{ location.city || '—' }}</td>
                <td>{{ location.state || '—' }}</td>
                <td v-if="!mergeMode">
                  <button
                      @click="editLocation(location)"
                      class="btn btn-sm btn-outline-primary me-2"
                      title="Edit"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                      @click="confirmDelete(location)"
                      class="btn btn-sm btn-outline-danger"
                      title="Delete"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div v-else-if="!loading && !showForm" class="alert alert-info">
      No source locations yet. Click "Add New Location" to create one.
    </div>

    <!-- Delete Confirmation Modal -->
    <app-modal :modal-in="showDeleteConfirm" @close="showDeleteConfirm = false" modal-class="modal-md">
      <template #header>Confirm Delete</template>
      <div>
        <p>Are you sure you want to delete <strong>{{ locationToDelete?.name }}</strong>?</p>
        <p class="text-muted small">This will not affect existing inventory items that reference this location.</p>
      </div>
      <div class="text-end mt-3">
        <button @click="showDeleteConfirm = false" class="btn btn-link">Cancel</button>
        <button @click="deleteLocation" class="btn btn-danger ms-2">Delete</button>
      </div>
    </app-modal>

    <!-- Merge Confirmation Modal -->
    <app-modal :modal-in="showMergeConfirm" @close="showMergeConfirm = false" modal-class="modal-md">
      <template #header>Merge Locations</template>
      <div>
        <p>Select which location to keep. The other locations will be merged into it and deleted.</p>

        <div class="list-group mb-3">
          <label
            v-for="location in selectedLocations"
            :key="location.id"
            class="list-group-item list-group-item-action d-flex align-items-center"
            :class="{ 'active': mainLocationId === location.id }"
          >
            <input
              type="radio"
              class="form-check-input me-2"
              :value="location.id"
              v-model="mainLocationId"
            />
            <div>
              <strong>{{ location.name }}</strong>
              <small v-if="location.city || location.state" class="d-block text-muted">
                {{ [location.city, location.state].filter(Boolean).join(', ') }}
              </small>
            </div>
          </label>
        </div>

        <div v-if="affectedItemsCount > 0" class="alert alert-info mb-0">
          <i class="bi bi-info-circle me-1"></i>
          <strong>{{ affectedItemsCount }}</strong> inventory item(s) will be updated to use the selected location.
        </div>
        <div v-else class="alert alert-secondary mb-0">
          <i class="bi bi-info-circle me-1"></i>
          No inventory items reference these locations. They will simply be deleted.
        </div>
      </div>
      <div class="text-end mt-3">
        <button @click="showMergeConfirm = false" class="btn btn-link" :disabled="mergeLoading">Cancel</button>
        <button @click="confirmMerge" class="btn btn-warning ms-2" :disabled="!mainLocationId || mergeLoading">
          <span v-if="mergeLoading" class="spinner-border spinner-border-sm me-1"></span>
          Confirm Merge
        </button>
      </div>
    </app-modal>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/app/components/Utils/LoadingSpinner.vue'
import StaticAlert from '@/app/components/Utils/StaticAlert.vue'
import SourceCategories from '@/app/components/inventory/SourceCategories.vue'
import AppModal from '@/app/components/Utils/AppModal.vue'

const store = useStore()

const loading = ref(false)
const showForm = ref(false)
const editingId = ref(null)
const message = ref('')
const showDeleteConfirm = ref(false)
const locationToDelete = ref(null)

// Merge mode state
const mergeMode = ref(false)
const selectedLocations = ref([])
const selectedCategoryId = ref(null)
const showMergeConfirm = ref(false)
const mainLocationId = ref(null)
const affectedItemsCount = ref(0)
const mergeLoading = ref(false)

const formData = ref({
  source_category_id: '',
  name: '',
  address: '',
  city: '',
  state: '',
  country: '',
  notes: ''
})

// Computed property to get flat list of all locations
const locationsList = computed(() => {
  const locations = []
  const sourceLocations = store.state.eshop.sourceLocations || []
  sourceLocations.forEach(category => {
    if (category.locations && Array.isArray(category.locations)) {
      category.locations.forEach(location => {
        locations.push(location)
      })
    }
  })
  return locations.sort((a, b) => a.name.localeCompare(b.name))
})

function getCategoryName(categoryId) {
  const sourceLocations = store.state.eshop.sourceLocations || []
  const category = sourceLocations.find(cat => cat.id === categoryId)
  return category?.name || 'Unknown'
}

function showAddForm() {
  editingId.value = null
  formData.value = {
    source_category_id: '',
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    notes: ''
  }
  showForm.value = true
}

function editLocation(location) {
  editingId.value = location.id
  formData.value = {
    source_category_id: location.category_id,
    name: location.name,
    address: location.address || '',
    city: location.city || '',
    state: location.state || '',
    country: location.country || '',
    notes: location.notes || ''
  }
  showForm.value = true
}

function cancelForm() {
  showForm.value = false
  editingId.value = null
  formData.value = {
    source_category_id: '',
    name: '',
    address: '',
    city: '',
    state: '',
    country: '',
    notes: ''
  }
}

async function saveLocation() {
  if (!formData.value.name || !formData.value.source_category_id) {
    message.value = 'Location name and category are required'
    return
  }

  loading.value = true
  message.value = ''

  try {
    const location = {
      ...formData.value,
      id: editingId.value
    }

    await store.dispatch('eshop/saveSourceLocation', { location })

    message.value = `Location ${editingId.value ? 'updated' : 'created'} successfully`
    cancelForm()
  } catch (error) {
    message.value = error.response?.data?.error || error.message || 'Failed to save location'
  } finally {
    loading.value = false
  }
}

function confirmDelete(location) {
  locationToDelete.value = location
  showDeleteConfirm.value = true
}

async function deleteLocation() {
  if (!locationToDelete.value) return

  loading.value = true
  showDeleteConfirm.value = false

  try {
    await store.dispatch('eshop/deleteSourceLocation', { id: locationToDelete.value.id })
    message.value = 'Location deleted successfully'
    locationToDelete.value = null
  } catch (error) {
    message.value = error.response?.data?.error || error.message || 'Failed to delete location'
  } finally {
    loading.value = false
  }
}

// Merge mode functions
function enterMergeMode() {
  mergeMode.value = true
  selectedLocations.value = []
  selectedCategoryId.value = null
  showForm.value = false
}

function exitMergeMode() {
  mergeMode.value = false
  selectedLocations.value = []
  selectedCategoryId.value = null
  mainLocationId.value = null
  affectedItemsCount.value = 0
}

function isLocationSelected(location) {
  return selectedLocations.value.some(l => l.id === location.id)
}

function isLocationDisabled(location) {
  return selectedCategoryId.value && location.category_id !== selectedCategoryId.value
}

function toggleLocationSelection(location) {
  const index = selectedLocations.value.findIndex(l => l.id === location.id)

  if (index >= 0) {
    // Remove from selection
    selectedLocations.value.splice(index, 1)

    // If no selections left, clear the category restriction
    if (selectedLocations.value.length === 0) {
      selectedCategoryId.value = null
    }
  } else {
    // Add to selection
    selectedLocations.value.push(location)

    // Set category restriction on first selection
    if (selectedLocations.value.length === 1) {
      selectedCategoryId.value = location.category_id
    }
  }
}

async function openMergeModal() {
  if (selectedLocations.value.length < 2) return

  mergeLoading.value = true

  try {
    const locationIds = selectedLocations.value.map(l => l.id)
    affectedItemsCount.value = await store.dispatch('eshop/getAffectedItemsCount', { locationIds })
    mainLocationId.value = selectedLocations.value[0].id
    showMergeConfirm.value = true
  } catch (error) {
    message.value = error.response?.data?.error || error.message || 'Failed to get affected items count'
  } finally {
    mergeLoading.value = false
  }
}

async function confirmMerge() {
  if (!mainLocationId.value || selectedLocations.value.length < 2) return

  mergeLoading.value = true

  try {
    // Build array with main ID first, then others
    const mainId = mainLocationId.value
    const otherIds = selectedLocations.value
      .filter(l => l.id !== mainId)
      .map(l => l.id)
    const locationIds = [mainId, ...otherIds]

    const result = await store.dispatch('eshop/mergeSourceLocations', { locationIds })

    message.value = `Merged ${result.deletedLocations} location(s) into "${result.mainLocation}". ${result.affectedItems} inventory item(s) updated.`
    showMergeConfirm.value = false
    exitMergeMode()
  } catch (error) {
    message.value = error.response?.data?.error || error.message || 'Failed to merge locations'
  } finally {
    mergeLoading.value = false
  }
}

// Load locations and categories on component mount
onMounted(() => {
  loading.value = true

  // Load source categories if not already loaded
  const sourceCategories = store.state.eshop.sourceCategories || []
  if (sourceCategories.length === 0) {
    store.dispatch('eshop/getSourceCategories')
  }

  // Load source locations
  store.dispatch('eshop/getSourceLocations')
    .finally(() => {
      loading.value = false
    })
})
</script>

<style scoped>
.table th {
  font-weight: 600;
  background-color: #f8f9fa;
}
</style>
